import '@mescius/wijmo.styles/wijmo.css';

import { Component, computed, effect, inject, input, model, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { Layout } from '@common/data-models/service/layout';
import { FieldDefValidations } from '@common/enums/enums';
import { CustomMergeManager, GridDragTypes } from '@common/utils/grid-utils';
import { getSecondsFromTimeSpan, getTimeSpanFromSeconds } from '@common/utils/timing-format';
import { addClass, removeClass } from '@mescius/wijmo';
import { WjComboBox } from '@mescius/wijmo.angular2.input';
import { CellRange, CellRangeEventArgs, CellType, FlexGrid, FormatItemEventArgs, HitTestInfo, Row } from '@mescius/wijmo.grid';
import { DataChangeEventArgs, ImmutabilityProvider } from '@mescius/wijmo.grid.immutable';
import { Store } from '@ngrx/store';
import { PSTFieldNames, PSTFieldNamesEnum } from '@src/suite/common/data-models/fields/pst-field-names';
import { RichTextSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectAutoCreateMosDeviceItems } from '@src/suite/live/store/selectors/live.selectors';
import { asapScheduler } from 'rxjs';

@Component({
    selector: 'st-ro-grid',
    templateUrl: './st-ro-grid.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./st-ro-grid.scss'],
    host: {
        class: 'st-ro-grid',
    },
})
export class StRoGridComponent {
    fieldDefValidations = FieldDefValidations;
    PSTFieldNames = PSTFieldNames;
    private provider: ImmutabilityProvider<StorytellingStoryRecord>;
    globalTablesService = inject(GlobalTablesService);
    store = inject(Store);

    autoCreateMosDeviceItems = this.store.selectSignal(selectAutoCreateMosDeviceItems)();
    i18n = this.store.selectSignal(selectI18N)();

    itemsSource = input<StorytellingStoryRecord[]>();
    record = input<StorytellingRundownRecord>();
    gridLayout = input<Layout>();
    // OverUnder text for timing bar
    timingOverUnder = input<string>('');
    selectedRows = model<StorytellingStoryRecord[]>([]);

    collectionChanged = output<{ propID: string; value: any; rowIndex: number; guid: string }>();
    collectionReordered = output<{ sequence: string[] }>();
    openStoryContent = output<{ guid: string }>();
    deleteStory = output<{ guid: string }>();
    addStoryToRundown = output<{ scriptDatabase: string; scriptPath: string; scriptGuid: string; insertBeforeGuid: string; propertiesToOverride: { [id: string]: any } }>();

    flexGrid = viewChild<FlexGrid>('flexGrid');

    // Text for selected row duration for timing bar
    timingSelection = signal<string>('');
    /** drag and drop bug fix ********************************************** */
    // an array with the selected rows indexes
    movedRows = signal<number[]>([]);
    // the state of ctrl key
    ctrlIsDown = signal<boolean>(false);
    mergedRowsRange = signal<{ [id: string]: CellRange }>({});
    /*********************************************************************** */

    flashingRowGuids = signal<string[]>([]);
    objectPlaceholderCreatePanelOpen = signal(false);

    usersDataMap = computed(() => {
        return Object.values(this.globalTablesService.Staff);
    });

    mosTypes = computed(() => {
        return [
            { id: '', title: '' },
            { id: this.i18n.OPCAudioContentLabel, title: this.i18n.OPCAudioContentLabel },
            { id: this.i18n.OPCStillContentLabel, title: this.i18n.OPCStillContentLabel },
            { id: this.i18n.OPCVideoContentLabel, title: this.i18n.OPCVideoContentLabel },
        ];
    });

    constructor() {
        effect(
            () => {
                if (this.itemsSource() && this.provider) {
                    const flashingItems = this.itemsSource().filter(val =>
                        this.provider.itemsSource.some(item => item?.Guid === val?.Guid && val?.Guid !== '0' && item?.ModTime?.date?.toISOString() !== val?.ModTime?.date?.toISOString())
                    );
                    if (flashingItems.length > 0) {
                        this.flashingRowGuids.set(flashingItems.map(val => val.Guid));
                    }
                    const collectionChanged = this.itemsSource()?.length !== this.provider.itemsSource?.length;
                    this.provider.itemsSource = this.itemsSource();
                    this.calculateMergeRanges();
                    if (collectionChanged) {
                        this.selectedRows.set(this.flexGrid().selectedRows.map(val => val.dataItem));
                    }
                }
            },
            { allowSignalWrites: true }
        );
    }

    onMosTypeChanged(index: number, mosType: WjComboBox) {
        const selectedType = this.mosTypes().find(val => val?.id?.toUpperCase() === this.autoCreateMosDeviceItems[index]?.objectType?.toUpperCase());
        mosType.selectedValue = selectedType?.id || this.mosTypes()[0].id;
    }

    formatItem(grid: FlexGrid, e: FormatItemEventArgs) {
        this.applyAlternatingRowsClass(grid, e);
    }

    calculateMergeRanges() {
        const mergedRowsRange = {};
        for (let r = 0; r < this.flexGrid().rows.length - 1; r++) {
            const range = this.flexGrid().getMergedRange(this.flexGrid().cells, r, 4);
            if (range) {
                mergedRowsRange[`${range._col}${range._col2}${range._row}${range.row2}`] = range;
            } else {
                mergedRowsRange[`${4}${4}${r}${r}`] = new CellRange(r, 4, r, 4);
            }
        }
        this.mergedRowsRange.set(mergedRowsRange);
    }

    applyAlternatingRowsClass(grid: FlexGrid, e: FormatItemEventArgs) {
        if (e.panel.cellType === CellType.Cell) {
            Object.keys(this.mergedRowsRange()).forEach((key, idx) => {
                const range = this.mergedRowsRange()[key];
                if (idx % 2 === 0) {
                    if (range.containsRow(e.row)) {
                        addClass(e.cell, 'alternating-row');
                    }
                }
            });
        }
    }

    createPlaceholder(mosDevice: WjComboBox, mosGroup: WjComboBox, mosType: WjComboBox, mosSlug: WjComboBox, cell) {
        const value = `${mosDevice.selectedValue};${mosGroup.selectedValue};${mosType.selectedValue};${mosSlug.selectedValue}`;
        this.collectionChanged.emit({
            propID: PSTFieldNamesEnum.ObjectPlaceholderCreate,
            value: { [PSTFieldNamesEnum.ObjectPlaceholderCreate]: value },
            rowIndex: cell.row.index,
            guid: cell.item.Guid,
        });
        mosDevice.selectedValue = '';
        mosGroup.selectedValue = '';
        mosType.selectedValue = '';
        mosSlug.selectedValue = '';
    }

    cancelPlaceholderCreate(mosDevice: WjComboBox, mosGroup: WjComboBox, mosType: WjComboBox, mosSlug: WjComboBox, _cell) {
        mosDevice.selectedValue = '';
        mosGroup.selectedValue = '';
        mosType.selectedValue = '';
        mosSlug.selectedValue = '';
    }

    initializedGrid(flexGrid: FlexGrid): void {
        flexGrid.mergeManager = new CustomMergeManager();
        this.provider = new ImmutabilityProvider(flexGrid, {
            itemsSource: this.itemsSource(),
            dataChanged: (s: ImmutabilityProvider, e: DataChangeEventArgs) => {
                const columnChanged = s.grid.columns[s.grid.selection.col];
                if (columnChanged.binding === PSTFieldNamesEnum.Slug) {
                    const mergeRange = flexGrid.getMergedRange(flexGrid.cells, e.itemIndex, 4);
                    for (let i = mergeRange._row; i <= mergeRange._row2; i++) {
                        this.collectionChanged.emit({ propID: columnChanged.binding, value: e.newItem, rowIndex: i, guid: flexGrid.rows[i].dataItem.Guid });
                    }
                } else {
                    this.collectionChanged.emit({ propID: columnChanged.binding, value: e.newItem, rowIndex: e.itemIndex, guid: e.newItem.Guid });
                }
            },
        });
        this.calculateMergeRanges();
        flexGrid.formatItem.addHandler((grid, event) => this.formatItem(grid, event));

        flexGrid.cellEditEnded.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            this.provider.grid.onRowEditEnded(e);
        });

        flexGrid.rowEditEnding.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            e.cancel = true;
        });
        flexGrid.rowEditEnded.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            if (e.cancel) {
                return;
            }
        });

        flexGrid.itemFormatter = (panel, r, c, cell) => {
            const row = panel.rows[r];
            const rowDataItem = panel.rows[r].dataItem;
            const column = panel.columns[c];

            // Enable drag and drop for cells
            if (r === panel.rows.length - 1) {
                cell.draggable = false;
                row.isReadOnly = true;
                // Remove edit/delete icons for empty last row
                if (c === 0 || c === 1) {
                    cell.textContent = '';
                }
            } else if (panel.cellType === CellType.Cell) {
                cell.draggable = true;
            }

            // first and second column c == 0 && c == 1 (edit, delete story)
            if ((c === 0 || c === 1) && r < flexGrid.rows.length - 1) {
                if (panel.cellType !== CellType.RowHeader && panel.cellType !== CellType.ColumnHeader) {
                    cell.style.cursor = 'pointer';
                }
            }

            // if (r === flexGrid.rows.length - 1) {
            //     cell.style.backgroundColor = 'white';
            //     return;
            // }
            // black line
            if (rowDataItem?.Guid === '0') {
                panel.rows[r].isReadOnly = true;
                panel.rows[r].allowDragging = false;
                if (panel.cellType === CellType.RowHeader || panel.cellType === CellType.Cell) {
                    row.isReadOnly = true;
                    cell.textContent = '';
                    addClass(cell, 'black-line');
                }
                return;
            }

            // story status
            if (rowDataItem) {
                if (column.binding === PSTFieldNames.SegStatus && panel.cellType === CellType.Cell) {
                    if (rowDataItem.Status !== undefined) {
                        if (rowDataItem.Status === '2') {
                            addClass(cell, 'yellow');
                        } else if (rowDataItem.Status === '3') {
                            addClass(cell, 'red');
                        } else if (rowDataItem.Status === '4') {
                            addClass(cell, 'green');
                        } else {
                            removeClass(cell, 'yellow');
                            removeClass(cell, 'red');
                            removeClass(cell, 'green');
                        }
                    }
                }
            }

            if (panel.cellType === CellType.ColumnHeader) {
                if (column.binding === PSTFieldNames.RowStatus || column.binding === PSTFieldNames.SegStatus) {
                    cell.textContent = '';
                }
            }

            // merged cells
            const range = flexGrid.getMergedRange(flexGrid.cells, r, 4);
            if (range) {
                const startRow = range._row;
                const endRow = range._row2;

                // iterate through every merged row
                // if even one row has no content, left col is yellow
                // red if all stories have content
                // green if all stories have content and approved
                let yellow = false;
                let red = false;
                let green = false;
                let transparent = false;
                for (let a = startRow; a <= endRow; a++) {
                    // const flexRow = panel.rows[a];
                    // const index = flexRow.dataIndex;
                    // const guid = flexRow.dataItem.GUID;
                    if (panel.rows[a] !== null && panel.rows[a] !== undefined) {
                        if (panel.rows[a].dataItem !== null && panel.rows[a].dataItem !== undefined) {
                            const rowData = panel.rows[a].dataItem;
                            const status = rowData.Status;
                            if (status === '2') {
                                yellow = true;
                            } else if (status === '3') {
                                if (yellow === true) {
                                    red = false;
                                } else {
                                    red = true;
                                }
                            } else if (!status) {
                                transparent = true;
                            }
                        }
                    }
                }
                if (yellow === false && red === false) {
                    green = true;
                } else {
                    green = false;
                }

                if (column.binding === PSTFieldNames.RowStatus && panel.cellType === CellType.Cell) {
                    //cell.style.borderBottom = "none";
                    if (yellow) {
                        addClass(cell, 'yellow');
                    } else if (red) {
                        addClass(cell, 'red');
                    } else if (transparent) {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    } else if (green) {
                        addClass(cell, 'green');
                    } else {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    }
                }
            } else {
                if (column.binding === PSTFieldNames.RowStatus && panel.cellType === CellType.Cell) {
                    const status = rowDataItem.Status;
                    if (status === '2') {
                        addClass(cell, 'yellow');
                    } else if (status === '3') {
                        addClass(cell, 'red');
                    } else if (status === '4') {
                        addClass(cell, 'green');
                    } else {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    }
                }
            }

            // break, float logic
            if (rowDataItem) {
                const hasBreak = rowDataItem.Break;
                const hasFloat = rowDataItem.Float;
                if (column.binding !== PSTFieldNames.RowStatus && column.binding !== PSTFieldNames.SegStatus) {
                    if (hasBreak && !hasFloat) {
                        addClass(cell, 'break');
                    } else if (hasBreak && hasFloat) {
                        addClass(cell, 'float');
                    } else if (hasFloat) {
                        addClass(cell, 'float');
                    }
                }
            }

            if (column.binding === PSTFieldNames.ObjectPlaceholderCreate) {
                addClass(cell, 'object-placeholder-create');
            }

            if (this.record()?.FreezePages && column.binding === PSTFieldNames.PageNum) {
                addClass(cell, 'frozen-page-numbers');
            } else {
                removeClass(cell, 'frozen-page-numbers');
            }

            if (rowDataItem?.isActualEnteredManually && column.binding === PSTFieldNames.Actual) {
                addClass(cell, 'actual-entered-manually');
            } else {
                removeClass(cell, 'actual-entered-manually');
            }

            // TODO: FIGURE THIS OUT
            if (rowDataItem != null) {
                if (column.binding !== PSTFieldNames.RowStatus && column.binding !== PSTFieldNames.SegStatus) {
                    if (this.flashingRowGuids().includes(rowDataItem.Guid)) {
                        setTimeout(() => {
                            const localCell = cell;
                            localCell.style.background = '#E96C14';
                            setTimeout(() => {
                                localCell.style.background = '';
                            }, 500);

                            setTimeout(() => {
                                localCell.style.background = '#E96C14';
                            }, 1000);

                            setTimeout(() => {
                                localCell.style.background = '';
                            }, 1500);

                            setTimeout(() => {
                                localCell.style.background = '#E96C14';
                            }, 2000);

                            setTimeout(() => {
                                // default row color
                                localCell.style.background = '';
                                this.flashingRowGuids.update(val => val.filter(guid => guid !== rowDataItem.Guid));
                            }, 2500);
                        }, 10);
                    }
                }
            }
        };
        this._dragDropListeners(flexGrid);
        this._selectionChangedListeners(flexGrid);
        /** drag and drop bug fix ********************************************** */
        // add keyboard events listeners to detect the ctrlkey state in selectionChanging event of the grid
        this._keyboardListeners(flexGrid);
        /*********************************************************************** */

        this.clearGridSelection(flexGrid);
    }

    openStory(flexGrid: FlexGrid, _event: any): void {
        this.openStoryContent.emit({ guid: flexGrid.selectedRows[0].dataItem.Guid });
    }

    deleteBasicContent(flexGrid: FlexGrid, _event: any): void {
        this.deleteStory.emit({ guid: flexGrid.selectedRows[0].dataItem.Guid });
    }

    /** drag and drop bug fix ********************************************** */
    private _dragDropListeners(s: FlexGrid) {
        // handle drag start
        s.addEventListener(
            s.hostElement,
            'dragstart',
            (e: DragEvent) => {
                const ht = s.hitTest(e);
                if (ht.cellType === CellType.Cell && ht.row < s.rows.length - 1) {
                    _createImage(s);
                    // collect row idnexes for drop event

                    this.movedRows.set(s.selectedRows.map(val => val._idx));
                    e.dataTransfer!.effectAllowed = 'move';
                    e.dataTransfer!.setData('text/plain', JSON.stringify({ actionType: GridDragTypes.REORDER.toString() }));
                }
            },
            true
        );
        // handle drag over
        s.addEventListener(
            s.hostElement,
            'dragover',
            (e: DragEvent) => {
                const dragRow = e.dataTransfer!.getData('rowIndex');
                if (dragRow != null) {
                    _moveImage(s, s.hitTest(e));
                }
                e.preventDefault();
                e.dataTransfer!.dropEffect = 'move';
            },
            true
        );
        // handle drag drop
        s.addEventListener(
            s.hostElement,
            'drop',
            (e: DragEvent) => {
                _removeImage();
                const ht = s.hitTest(e);
                const { scriptDatabase, scriptPath, scriptGuid, actionType, text, textCommands } = JSON.parse(e.dataTransfer!.getData('text/plain'));
                if (ht.cellType === CellType.Cell) {
                    if (actionType === GridDragTypes.REORDER) {
                        // because the rows do not follow we assume that first selected is a source index
                        const sourceItemIndex = this.movedRows()[0];
                        const destinationItemIndex = ht.row;

                        // If cells have not been moved
                        if (sourceItemIndex === destinationItemIndex) return;

                        const newSequence: string[] = [];

                        this.flexGrid().rows.forEach((row: any, i: number) => {
                            if (i === destinationItemIndex) {
                                for (let j = 0; j < this.movedRows().length; j++) {
                                    newSequence.push(this.flexGrid().rows[this.movedRows()[j]].dataItem.Guid);
                                }
                            }
                            if (!this.movedRows().includes(i)) {
                                const item = this.flexGrid().rows[i].dataItem;
                                if (Object.keys(item).length !== 0) {
                                    newSequence.push(item.Guid);
                                }
                            }
                        });
                        this.collectionReordered.emit({ sequence: newSequence });

                        this.clearGridSelection(s);
                        this.movedRows.set([]);
                    } else if (actionType === GridDragTypes.ADD) {
                        const propertiesToOverride = {};
                        propertiesToOverride[PSTFieldNamesEnum.PSTDraftText] = RichTextSerializer.Instance.serialize(text);
                        if (textCommands) {
                            const textCommandKeys = Object.keys(textCommands);
                            const textCommandValues = Object.values(textCommands);
                            if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                propertiesToOverride['TextCommandKeys'] = textCommandKeys;
                                propertiesToOverride['TextCommandValues'] = textCommandValues;
                            }
                        }
                        this.addStoryToRundown.emit({ scriptDatabase, scriptPath, scriptGuid, insertBeforeGuid: s.rows[ht.row].dataItem.Guid, propertiesToOverride });
                    }

                    // e.preventDefault();
                }
            },
            true
        );

        // drop preview
        let div: HTMLDivElement;
        function _createImage(grid: FlexGrid) {
            div = document.createElement('div');
            div.className = 'wj-marker';
            grid.rowHeaders.hostElement.appendChild(div);
            return div;
        }

        function _moveImage(grid: FlexGrid, ht: HitTestInfo) {
            if (ht.cellType === CellType.Cell) {
                if (div == null) {
                    _createImage(grid);
                }
                let dropRow = ht.row;

                const range = grid.getMergedRange(grid.cells, ht.row, 4);
                if (range != null) {
                    dropRow = range._row;
                }

                const rect = grid.cells.getCellBoundingRect(dropRow, 0, true);

                div.style.top = rect.top + window.scrollY + 'px';
                div.style.left = 0 + window.scrollX + 'px';
            }
        }

        function _removeImage() {
            if (div != null) {
                if (div.parentElement != null) div.parentElement!.removeChild(div);
            }
        }
    }

    private _selectionChangedListeners(grid: FlexGrid) {
        grid.selectionChanged.addHandler((gridparam: FlexGrid, _e: CellRangeEventArgs) => {
            this.selectedRows.set(gridparam.selectedRows.map(val => val.dataItem));
            if (gridparam.selectedRows) {
                let timingSeconds = 0;
                gridparam.selectedRows.forEach((row: Row) => {
                    const actual = getSecondsFromTimeSpan(row.dataItem.Actual);
                    const estimated = getSecondsFromTimeSpan(row.dataItem.Estimated);

                    if (actual !== 0) {
                        timingSeconds += actual;
                    } else if (estimated !== 0) {
                        timingSeconds += estimated;
                    }
                });

                asapScheduler.schedule(() => {
                    this.timingSelection.set(getTimeSpanFromSeconds(timingSeconds));
                });
            } else {
                asapScheduler.schedule(() => {
                    this.timingSelection.set('');
                });
            }
        });

        grid.selectionChanging.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            // if the given row with the event is selected we "killing" the event with cancel=true to prevent de-selecting of row
            if (this.checkSelected(e.row, s)) {
                if (this.ctrlIsDown()) {
                    e.cancel = true;
                }
            }
        });
    }
    /****************************************************************************** */

    private _keyboardListeners(s: FlexGrid) {
        s.addEventListener(
            s.hostElement,
            'keydown',
            (e: KeyboardEvent) => {
                this.ctrlIsDown.set(e.ctrlKey);
            },
            true
        );
        s.addEventListener(
            s.hostElement,
            'keyup',
            (e: KeyboardEvent) => {
                this.ctrlIsDown.set(e.ctrlKey);
            },
            true
        );
    }

    /**
     * Checks the given row if exists in selected rows array
     * @param row row number
     * @param grid the grid component
     * @returns  true or false
     */
    checkSelected(row: number, grid: FlexGrid): any {
        let result = false;
        grid.selectedRows.forEach(val => {
            if (row === val._idx) {
                result = true;
            }
        });
        return result;
    }

    private clearGridSelection(flexGrid: FlexGrid): void {
        if (flexGrid) {
            flexGrid.select(-1, -1);
        }
    }
}
